import { Bot, CreditCard, Repeat, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  SubtleTabs,
  SubtleTabsContent,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import type { ContractAddon } from '../../services/contractService'
import type { EditableCharge } from '../../types/customerDetail.types'
import { ChargeTable } from './ChargeTable'

interface AddonDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  addon: ContractAddon
  contractId: string
}

export function AddonDetailsDialog({
  open,
  onOpenChange,
  addon,
}: AddonDetailsDialogProps) {
  const [activeTab, setActiveTab] = useState('fixed')

  // Convert addon data to editable charges for display
  const editableCharges: EditableCharge[] = []

  // Process charges (fixed/recurring)
  if (addon.charges) {
    // biome-ignore lint/suspicious/noExplicitAny: required for dynamic addon charge data
    addon.charges.forEach((charge: any) => {
      const category =
        charge.charge_type === 'one_time'
          ? 'fixed'
          : charge.charge_type === 'recurring'
            ? 'recurring'
            : 'fixed'

      editableCharges.push({
        id: charge.id,
        category,
        type: charge.charge_type,
        name: charge.name,
        description: charge.description || '',
        unitPrice: parseFloat(charge.amount) || 0,
        originalPrice: parseFloat(charge.amount) || 0,
        billingTiming: 'arrears',
        isEdited: false,
        pricingModel: 'flat',
        includedUnits: 0,
        minCommitment: 0,
        tiers: null,
        included_units: 0,
        included_quantity: 0,
        minimum_commitment: 0,
        minimum_commitment_units: 0,
        unit_price: parseFloat(charge.amount) || 0,
      } as unknown as EditableCharge)
    })
  }

  // Process agent signals
  if (addon.agents) {
    // biome-ignore lint/suspicious/noExplicitAny: required for dynamic addon agent data
    addon.agents.forEach((agent: any) => {
      if (agent.signal_configurations) {
        // biome-ignore lint/suspicious/noExplicitAny: required for dynamic signal config data
        ;(agent.signal_configurations as any[]).forEach(
          // biome-ignore lint/suspicious/noExplicitAny: required for dynamic signal config data
          (signal: any) => {
            const tiers =
              signal.tiers && signal.tiers.length > 0
                ? // biome-ignore lint/suspicious/noExplicitAny: required for dynamic tier data
                  signal.tiers.map((tier: any) => ({
                    firstUnit: tier.minUnits || 1,
                    lastUnit:
                      tier.maxUnits === null ? 999999999 : tier.maxUnits,
                    minUnits: tier.minUnits || 1,
                    maxUnits: tier.maxUnits,
                    unitPrice: parseFloat(String(tier.unitPrice)) || 0,
                    unit_price: parseFloat(String(tier.unitPrice)) || 0,
                    included_quantity: 0,
                  }))
                : null

            editableCharges.push({
              id: signal.signal_id,
              category: 'agent',
              type: 'usage_agent',
              name: signal.signal_name,
              description: '',
              unitPrice: parseFloat(signal.unit_price) || 0,
              originalPrice: parseFloat(signal.unit_price) || 0,
              billingTiming: 'arrears',
              isEdited: false,
              pricingModel:
                signal.pricing_model === 'package'
                  ? 'tiered'
                  : signal.pricing_model || 'flat',
              includedUnits: signal.included_units || 0,
              minCommitment: signal.minimum_commitment_units || 0,
              agentId: agent.agent_id,
              signalId: signal.signal_id,
              signalName: signal.signal_name,
              kind: signal.metadata?.kind || 'task',
              tiers,
              included_units: signal.included_units || 0,
              included_quantity: signal.included_units || 0,
              minimum_commitment: signal.minimum_commitment_units || 0,
              minimum_commitment_units: signal.minimum_commitment_units || 0,
              unit_price: parseFloat(signal.unit_price) || 0,
            } as unknown as EditableCharge)
          }
        )
      }
    })
  }

  // Process features
  if (addon.features) {
    // biome-ignore lint/suspicious/noExplicitAny: required for dynamic addon feature data
    addon.features.forEach((feature: any) => {
      const tiers = feature.tier_pricing
        ? // biome-ignore lint/suspicious/noExplicitAny: required for dynamic tier data
          feature.tier_pricing.map((tier: any) => ({
            firstUnit: tier.firstUnit || 1,
            lastUnit: tier.lastUnit === null ? 999999999 : tier.lastUnit,
            minUnits: tier.firstUnit || 1,
            maxUnits: tier.lastUnit,
            unitPrice: tier.perUnitPrice || 0,
            unit_price: tier.perUnitPrice || 0,
            included_quantity: 0,
          }))
        : null

      editableCharges.push({
        id: feature.id,
        category: 'feature',
        type: 'usage_feature',
        name: feature.metadata?.feature_name || `Feature ${feature.feature_id}`,
        description: feature.metadata?.description || '',
        unitPrice: parseFloat(feature.overage_rate) || 0,
        originalPrice: parseFloat(feature.overage_rate) || 0,
        billingTiming: 'arrears',
        isEdited: false,
        pricingModel: feature.metadata?.pricing_model || 'flat',
        includedUnits: parseFloat(feature.included_quantity) || 0,
        minCommitment: parseFloat(feature.minimum_commitment_units) || 0,
        featureId: feature.feature_id,
        tiers,
        included_units: parseFloat(feature.included_quantity) || 0,
        included_quantity: parseFloat(feature.included_quantity) || 0,
        minimum_commitment: parseFloat(feature.minimum_commitment_units) || 0,
        minimum_commitment_units:
          parseFloat(feature.minimum_commitment_units) || 0,
        unit_price: parseFloat(feature.overage_rate) || 0,
      } as unknown as EditableCharge)
    })
  }

  const isLoading = false

  // Filter charges by category
  const fixedCharges = editableCharges.filter(c => c.category === 'fixed')
  const recurringCharges = editableCharges.filter(
    c => c.category === 'recurring'
  )
  const agentCharges = editableCharges.filter(c => c.category === 'agent')
  const featureCharges = editableCharges.filter(c => c.category === 'feature')

  // Calculate total charges based on the addon data structure
  const _totalCharges = useMemo(() => {
    let total = 0

    // Add charges (fixed/recurring)
    if (addon.charges) {
      total += addon.charges.reduce(
        // biome-ignore lint/suspicious/noExplicitAny: required for dynamic charge data
        (sum: number, charge: any) =>
          sum + (parseFloat(String(charge.amount)) || 0),
        0
      )
    }

    // Add agent signal minimum commitments with proper tier pricing
    if (addon.agents) {
      // biome-ignore lint/suspicious/noExplicitAny: required for dynamic addon agent data
      addon.agents.forEach((agent: any) => {
        if (agent.signal_configurations) {
          agent.signal_configurations.forEach(
            // biome-ignore lint/suspicious/noExplicitAny: required for dynamic signal config data
            (signal: any) => {
              const minCommitment = signal.minimum_commitment_units || 0
              if (minCommitment > 0) {
                let cost = 0

                if (
                  signal.pricing_model === 'tiered' &&
                  signal.tiers &&
                  signal.tiers.length > 0
                ) {
                  // Tiered pricing: distribute units across tiers
                  let remainingUnits = minCommitment
                  for (const tier of signal.tiers) {
                    if (remainingUnits <= 0) break
                    const tierMax =
                      tier.maxUnits === null || tier.maxUnits === undefined
                        ? Infinity
                        : tier.maxUnits
                    const tierMin = tier.minUnits || 1
                    const tierRange =
                      tierMax === Infinity
                        ? remainingUnits
                        : tierMax - tierMin + 1
                    const unitsInThisTier = Math.min(remainingUnits, tierRange)
                    const tierPrice = parseFloat(String(tier.unitPrice)) || 0
                    cost += unitsInThisTier * tierPrice
                    remainingUnits -= unitsInThisTier
                  }
                } else if (
                  signal.pricing_model === 'volume' &&
                  signal.tiers &&
                  signal.tiers.length > 0
                ) {
                  // Volume pricing: find applicable tier and apply rate to all units
                  const applicableTier = signal.tiers.find(
                    // biome-ignore lint/suspicious/noExplicitAny: required for dynamic tier data
                    (tier: any) =>
                      minCommitment >= (tier.minUnits || 1) &&
                      (tier.maxUnits === null || minCommitment <= tier.maxUnits)
                  )
                  if (applicableTier) {
                    cost =
                      minCommitment *
                      (parseFloat(String(applicableTier.unitPrice)) || 0)
                  }
                } else {
                  // Flat pricing
                  cost = minCommitment * (parseFloat(signal.unit_price) || 0)
                }

                total += cost
              }
            }
          )
        }
      })
    }

    // Add feature minimum commitments with proper tier pricing
    if (addon.features) {
      // biome-ignore lint/suspicious/noExplicitAny: required for dynamic addon feature data
      addon.features.forEach((feature: any) => {
        const minCommitment = parseFloat(feature.minimum_commitment_units) || 0
        if (minCommitment > 0) {
          let cost = 0

          if (
            feature.metadata?.pricing_model === 'tiered' &&
            feature.tier_pricing
          ) {
            // Tiered pricing: distribute units across tiers
            let remainingUnits = minCommitment
            for (const tier of feature.tier_pricing) {
              if (remainingUnits <= 0) break
              const tierMax =
                tier.lastUnit === null || tier.lastUnit === undefined
                  ? Infinity
                  : tier.lastUnit
              const tierMin = tier.firstUnit || 1
              const tierRange =
                tierMax === Infinity ? remainingUnits : tierMax - tierMin + 1
              const unitsInThisTier = Math.min(remainingUnits, tierRange)
              const tierPrice = tier.perUnitPrice || 0
              cost += unitsInThisTier * tierPrice
              remainingUnits -= unitsInThisTier
            }
          } else if (
            feature.metadata?.pricing_model === 'volume' &&
            feature.tier_pricing
          ) {
            // Volume pricing: find applicable tier and apply rate to all units
            const applicableTier = feature.tier_pricing.find(
              // biome-ignore lint/suspicious/noExplicitAny: required for dynamic tier data
              (tier: any) =>
                minCommitment >= (tier.firstUnit || 1) &&
                (tier.lastUnit === null || minCommitment <= tier.lastUnit)
            )
            if (applicableTier) {
              cost = minCommitment * (applicableTier.perUnitPrice || 0)
            }
          } else {
            // Flat pricing
            cost = minCommitment * (parseFloat(feature.overage_rate) || 0)
          }

          total += cost
        }
      })
    }

    return total
  }, [addon])

  const handleChargeUpdate = (_chargeId: string, _newPrice: number) => {
    // TODO: Implement charge update
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[95vw] sm:max-w-[95vw] lg:max-w-[1200px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <div className='flex items-center justify-between'>
            <DialogTitle className='text-xl'>
              {addon.name} - Addon Details
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Charges Breakdown */}
          {isLoading ? (
            <div className='text-center py-8 text-muted-foreground'>
              Loading addon charges...
            </div>
          ) : editableCharges.length === 0 ? (
            <div className='text-center py-8 text-muted-foreground'>
              No charges found for this addon.
            </div>
          ) : (
            <div>
              <h3 className='text-lg font-title mb-4'>Charges Breakdown</h3>

              <SubtleTabs
                value={activeTab}
                onValueChange={setActiveTab}
                className='w-full'
              >
                <SubtleTabsList className='grid w-full grid-cols-4'>
                  <SubtleTabsTrigger
                    value='fixed'
                    className='flex items-center gap-2'
                  >
                    <CreditCard className='w-4 h-4' />
                    Fixed ({fixedCharges.length})
                  </SubtleTabsTrigger>
                  <SubtleTabsTrigger
                    value='recurring'
                    className='flex items-center gap-2'
                  >
                    <Repeat className='w-4 h-4' />
                    Recurring ({recurringCharges.length})
                  </SubtleTabsTrigger>
                  <SubtleTabsTrigger
                    value='agents'
                    className='flex items-center gap-2'
                  >
                    <Bot className='w-4 h-4' />
                    Agents ({agentCharges.length})
                  </SubtleTabsTrigger>
                  <SubtleTabsTrigger
                    value='features'
                    className='flex items-center gap-2'
                  >
                    <Zap className='w-4 h-4' />
                    Features ({featureCharges.length})
                  </SubtleTabsTrigger>
                </SubtleTabsList>

                <SubtleTabsContent value='fixed' className='mt-6'>
                  {fixedCharges.length > 0 ? (
                    <ChargeTable
                      charges={fixedCharges}
                      chargeType='fixed'
                      onChargeUpdate={handleChargeUpdate}
                    />
                  ) : (
                    <div className='text-center py-12 text-muted-foreground'>
                      <CreditCard className='w-12 h-12 mx-auto mb-4 opacity-50' />
                      <p>No fixed costs for this addon</p>
                    </div>
                  )}
                </SubtleTabsContent>

                <SubtleTabsContent value='recurring' className='mt-6'>
                  {recurringCharges.length > 0 ? (
                    <ChargeTable
                      charges={recurringCharges}
                      chargeType='recurring'
                      onChargeUpdate={handleChargeUpdate}
                    />
                  ) : (
                    <div className='text-center py-12 text-muted-foreground'>
                      <Repeat className='w-12 h-12 mx-auto mb-4 opacity-50' />
                      <p>No recurring charges for this addon</p>
                    </div>
                  )}
                </SubtleTabsContent>

                <SubtleTabsContent value='agents' className='mt-6'>
                  {agentCharges.length > 0 ? (
                    <ChargeTable
                      charges={agentCharges}
                      chargeType='agents'
                      onChargeUpdate={handleChargeUpdate}
                    />
                  ) : (
                    <div className='text-center py-12 text-muted-foreground'>
                      <Bot className='w-12 h-12 mx-auto mb-4 opacity-50' />
                      <p>No agent charges for this addon</p>
                    </div>
                  )}
                </SubtleTabsContent>

                <SubtleTabsContent value='features' className='mt-6'>
                  {featureCharges.length > 0 ? (
                    <ChargeTable
                      charges={featureCharges}
                      chargeType='features'
                      onChargeUpdate={handleChargeUpdate}
                    />
                  ) : (
                    <div className='text-center py-12 text-muted-foreground'>
                      <Zap className='w-12 h-12 mx-auto mb-4 opacity-50' />
                      <p>No feature charges for this addon</p>
                    </div>
                  )}
                </SubtleTabsContent>
              </SubtleTabs>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
